'use client';

/**
 * useConfirm + ConfirmProvider — imperative API for branded confirmation.
 *
 * Drop-in replacement for `window.confirm()` patterns. The shape mirrors the
 * built-in so migration is a single-line swap:
 *
 *   // Before
 *   if (!confirm(`Remove ${name}?`)) return;
 *   await api.removeMember(id);
 *
 *   // After
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: `Remove ${name}?`, variant: 'destructive' }))) return;
 *   await api.removeMember(id);
 *
 * Architecture:
 *  - One <ConfirmDialog> instance lives in <ConfirmProvider> at app root.
 *  - useConfirm() returns a referentially-stable async function — does not
 *    re-render consumers when the dialog opens/closes.
 *  - Resolves the awaited promise with the user's choice (true/false).
 *  - ESC or click-outside resolves false.
 */

import * as React from 'react';
import { ConfirmDialog, type ConfirmDialogProps } from '@/components/ui/confirm-dialog';

export type ConfirmOptions = Pick<
  ConfirmDialogProps,
  'title' | 'description' | 'confirmLabel' | 'cancelLabel' | 'variant'
>;

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = React.createContext<ConfirmFn | null>(null);

interface PendingConfirm {
  options: ConfirmOptions;
  resolve: (ok: boolean) => void;
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = React.useState<PendingConfirm | null>(null);
  // Stable ref so the confirm function never changes identity across renders.
  const pendingRef = React.useRef<PendingConfirm | null>(null);
  pendingRef.current = pending;

  const confirm = React.useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      setPending({ options, resolve });
    });
  }, []);

  const handleConfirm = React.useCallback(() => {
    pendingRef.current?.resolve(true);
    setPending(null);
  }, []);

  const handleOpenChange = React.useCallback((open: boolean) => {
    if (!open) {
      // Cancel / ESC / outside-click all resolve false.
      pendingRef.current?.resolve(false);
      setPending(null);
    }
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={pending !== null}
        onOpenChange={handleOpenChange}
        title={pending?.options.title ?? ''}
        description={pending?.options.description}
        confirmLabel={pending?.options.confirmLabel}
        cancelLabel={pending?.options.cancelLabel}
        variant={pending?.options.variant}
        onConfirm={handleConfirm}
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = React.useContext(ConfirmContext);
  if (ctx === null) {
    throw new Error(
      'useConfirm must be used within a <ConfirmProvider>. Mount it once at the app root (src/app/layout.tsx).',
    );
  }
  return ctx;
}
